// Updated import paths for React app structure - VerificationProcessor.js adapted from FaceTec sample
import { Config } from "../../Config.js";

// Simple mock for DeveloperStatusMessages for React
const DeveloperStatusMessages = {
  logMessage: (message) => {
    console.log(`FaceTec SampleApp: ${message}`);
  }
};

//
// Welcome to the annotated FaceTec Device SDK core code for performing secure Verification against a previously enrolled user.
//
export var VerificationProcessor = /** @class */ (function () {
    function VerificationProcessor(sessionToken, userId, callback) {
        var _this = this;
        this.latestNetworkRequest = new XMLHttpRequest();
        
        //
        // Part 10:  This function gets called after the FaceTec SDK is completely done.
        //
        this.onFaceTecSDKCompletelyDone = function () {
            _this.success = _this.latestSessionResult.isCompletelyDone;
            
            // Log success message
            if (_this.success) {
                DeveloperStatusMessages.logMessage("Liveness-proven Face Verified");
            }
            
            // Call the callback with the result for React integration
            if (_this.callback) {
                _this.callback({
                    success: _this.success,
                    sessionResult: _this.latestSessionResult,
                    error: _this.success ? null : "Verification failed"
                });
            }
        };

        // Helper function to ensure the session is only cancelled once
        this.cancelDueToNetworkError = function (networkErrorMessage, faceScanResultCallback) {
            if (_this.cancelledDueToNetworkError === false) {
                console.error(networkErrorMessage);
                _this.cancelledDueToNetworkError = true;
                faceScanResultCallback.cancel();
            }
        };

        //
        // DEVELOPER NOTE:  This public convenience method is for demonstration purposes only so the Sample App can get information about what is happening in the processor.
        //
        this.isSuccess = function () {
            return _this.success;
        };

        //
        // DEVELOPER NOTE:  These properties are for demonstration purposes only so the Sample App can get information about what is happening in the processor.
        //
        this.success = false;
        this.userId = userId;  // Store userId for verification lookup
        this.callback = callback;  // Store callback for React integration
        this.latestSessionResult = null;
        this.cancelledDueToNetworkError = false;

        //
        // Part 1:  Starting the FaceTec Session
        //
        new window.FaceTecSDK.FaceTecSession(this, sessionToken);
    }

    //
    // Part 2:  Handling the Result of a FaceScan
    //
    VerificationProcessor.prototype.processSessionResultWhileFaceTecSDKWaits = function (sessionResult, faceScanResultCallback) {
        var _this = this;
        this.latestSessionResult = sessionResult;

        //
        // Part 3:  Handles early exit scenarios where there is no FaceScan to handle -- i.e. User Cancellation, Timeouts, etc.
        //
        if (sessionResult.status !== window.FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
            this.latestNetworkRequest.abort();
            faceScanResultCallback.cancel();
            return;
        }

        // IMPORTANT:  FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully DOES NOT mean the Verification was Successful.
        // It simply means the User completed the Session and a 3D FaceScan was created.  You still need to perform the Verification on your Servers.

        //
        // Part 4:  Get essential data off the FaceTecSessionResult
        //
        var parameters = {
            user_id: _this.userId,  // Pass the user ID for 1:1 verification lookup
            face_scan: sessionResult.faceScan,
            audit_trail_image: sessionResult.auditTrail[0],
            low_quality_audit_trail_image: sessionResult.lowQualityAuditTrail[0],
            user_agent: window.FaceTecSDK.createFaceTecAPIUserAgentString(sessionResult.sessionId)
        };

        //
        // Part 5:  Make the Networking Call to Your Servers (Xano endpoint)
        //
        this.latestNetworkRequest = new XMLHttpRequest();
        this.latestNetworkRequest.open('POST', Config.VerificationURL);  // New Xano verification endpoint
        this.latestNetworkRequest.setRequestHeader("Content-Type", "application/json");

        this.latestNetworkRequest.onreadystatechange = function () {
            //
            // Part 6:  In our Sample, we evaluate a boolean response and treat true as was successfully processed and should proceed to next step,
            // and handle all other responses by cancelling out.
            //
            if (_this.latestNetworkRequest.readyState === XMLHttpRequest.DONE) {
                try {
                    var responseJSON = JSON.parse(_this.latestNetworkRequest.responseText);
                    var scanResultBlob = responseJSON.scanResultBlob;

                    // In v9.2.0+, we key off a new property called wasProcessed to determine if we successfully processed the Session result on the Server.
                    if (responseJSON.wasProcessed === true && responseJSON.error === false) {
                        // Demonstrates dynamically setting the Success Screen Message.
                        window.FaceTecSDK.FaceTecCustomization.setOverrideResultScreenSuccessMessage("3D Liveness Proven\nFace Verified");
                        
                        // In v9.2.0+, simply pass in scanResultBlob to the proceedToNextStep function to advance the User flow.
                        faceScanResultCallback.proceedToNextStep(scanResultBlob);
                    }
                    else {
                        // CASE:  UNEXPECTED response from API.  Our Sample Code keys off a wasProcessed boolean on the root of the JSON object
                        if (responseJSON.error === true && responseJSON.errorMessage != null) {
                            _this.cancelDueToNetworkError(responseJSON.errorMessage, faceScanResultCallback);
                        }
                        else {
                            _this.cancelDueToNetworkError("Unexpected API response, cancelling out.", faceScanResultCallback);
                        }
                    }
                }
                catch (e) {
                    // CASE:  Parsing the response into JSON failed
                    _this.cancelDueToNetworkError("Exception while handling API response, cancelling out.", faceScanResultCallback);
                }
            }
        };

        this.latestNetworkRequest.onerror = function () {
            // CASE:  Network Request itself is erroring
            _this.cancelDueToNetworkError("XHR error, cancelling.", faceScanResultCallback);
        };

        //
        // Part 7:  Demonstrates updating the Progress Bar based on the progress event.
        //
        this.latestNetworkRequest.upload.onprogress = function (event) {
            var progress = event.loaded / event.total;
            faceScanResultCallback.uploadProgress(progress);
        };

        //
        // Part 8:  Actually send the request.
        //
        var jsonStringToUpload = JSON.stringify(parameters);
        this.latestNetworkRequest.send(jsonStringToUpload);

        //
        // Part 9:  For better UX, update the User if the upload is taking a while.
        //
        window.setTimeout(function () {
            if (_this.latestNetworkRequest.readyState === XMLHttpRequest.DONE) {
                return;
            }
            faceScanResultCallback.uploadMessageOverride("Still Uploading...");
        }, 6000);
    };

    return VerificationProcessor;
}());